import { apiFetch } from "./api";

export type SupportedLocale = "en" | "fr";
export type Theme = "light" | "dark";

export interface MobileNavbarSettings {
  enabled: boolean;
  maxButtons: number; // 3-6; beyond, a "More" button groups the rest
  items: string[]; // ordered nav item keys (dashboard is implicit and always first)
}

export interface SessionSettings {
  locale: SupportedLocale;
  theme: Theme;
  colorTheme: string;
  dateFormat: string;
  perPage: number;
  listerViewMode?: "table" | "cards" | "grid";
  listerHiddenColumns?: Record<string, string[]>; // model -> hidden columns
  recentHistoryMaxItems?: number; // Default: 5, max items visible in sidebar
  mobileNavbar?: MobileNavbarSettings; // identity-level, stored in extra jsonb
  [key: string]: any;
}

const STORAGE_KEY = "portal-settings";
const COOKIE_NAME = "portal-settings";

const DEFAULT_SETTINGS: SessionSettings = {
  locale: "fr",
  theme: "light",
  colorTheme: "default",
  dateFormat: "YYYY-MM-DD",
  perPage: 20
};

/**
 * Get the current session settings from localStorage with defaults.
 */
export function getSessionSettings(): SessionSettings {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    // Fallback to legacy locale if settings are missing
    const legacyLocale = localStorage.getItem("portal-locale");
    if (legacyLocale === "en" || legacyLocale === "fr") {
      return { ...DEFAULT_SETTINGS, locale: legacyLocale as SupportedLocale };
    }
    return { ...DEFAULT_SETTINGS };
  }

  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

/**
 * Persist session settings to localStorage, cookie, AND server.
 *
 * Settings are saved in three layers:
 * 1. localStorage - Immediate, for instant UI response
 * 2. Cookie - For Blade inline script (prevents flash on page load)
 * 3. Server API - For database persistence (shared/device split)
 */
export async function setSessionSettings(
  settings: Partial<SessionSettings>
): Promise<void> {
  const current = getSessionSettings();
  const updated = { ...current, ...settings };

  // Update localStorage immediately for instant UI response
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

  // Also update legacy locale for backward compatibility
  if (settings.locale) {
    localStorage.setItem("portal-locale", settings.locale);
  }

  // Sync to cookie for Blade inline script
  syncSettingsToCookie(updated);

  // Persist to server (fire and forget with error handling)
  await syncSettingsToServer(settings);
}

/**
 * Sync settings to the JSON-encoded cookie for the backend inline script.
 */
export function syncSettingsToCookie(settings: SessionSettings): void {
  const json = JSON.stringify(settings);
  // 1 year expiry
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(json)}; path=/; max-age=31536000; SameSite=Lax`;

  // Also sync legacy cookie if locale is present
  if (settings.locale) {
    document.cookie = `portal-locale=${settings.locale}; path=/; max-age=31536000; SameSite=Lax`;
  }
}

/**
 * Sync settings to the server API for database persistence.
 *
 * This is a fire-and-forget operation - if it fails, localStorage/cookie still work.
 * Settings are automatically split into shared (identity) and device (session) on the server.
 */
async function syncSettingsToServer(
  settings: Partial<SessionSettings>
): Promise<void> {
  try {
    await apiFetch("/auth/settings", {
      method: "POST",
      body: JSON.stringify(settings),
    });
  } catch (error) {
    // Silent fail - localStorage/cookie continue to work as fallback
    console.warn("[Portal] Failed to sync settings to server:", error);
  }
}

/**
 * Fetch settings from the server.
 *
 * This is useful for getting the latest settings after login,
 * as the server may have more up-to-date shared settings.
 */
export async function fetchServerSettings(): Promise<SessionSettings | null> {
  try {
    const data = await apiFetch<any>("/auth/settings", {
      method: "GET",
    });

    return { ...DEFAULT_SETTINGS, ...data };
  } catch (error) {
    console.warn("[Portal] Failed to fetch settings from server:", error);
    return null;
  }
}
